import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "@hello-pangea/dnd";
import styles from "./scenarioTestCases.module.scss";
import { MethodType } from "../../../shared/components/methodType/methodType";
import {
  Scenario,
  ScenarioTestCase
} from "../../../types/scenarioTypes";
import { useTranslation } from "react-i18next";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import {
  deleteScenarioExecuteResponse,
  updateScenarioSelectedTestCase
} from "../../../store/slices/scenarioSlice";
import dragIcon from '../../../shared/images/drag.svg';
import { ReactComponent as DeleteIcon } from "../../../shared/images/delete.svg";
import { useUpdateScenarioRequestsMutation } from "../../../api/scenarioService";
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { setUpdateModal } from "../../../store/slices/mainSlice";
import { AddTestCasesToScenarioModal } from "../../addTestCasesToScenarioModal/addTestCasesToScenarioModal";
import { ReactComponent as AddIcon } from "../../../shared/images/add.svg";
import {
  TestCase,
  TestCaseTemplate
} from "../../../types/testCaseTemplateTypes";
import spinner from '../../../shared/images/spinner.gif';
import {
  getTemplateAndTestCaseByTestCaseId,
  getTransformTestCase
} from "../../../utils/testCaseUtils";

type ScenarioTestCasesProps = {
  scenario: Scenario;
  testCaseTemplates?: TestCaseTemplate[];
  refetchScenarios: () => void;
  cancelChanges: () => void;
}

export const ScenarioTestCases = ({scenario, testCaseTemplates, refetchScenarios, cancelChanges}: ScenarioTestCasesProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation();

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);
  const selectedTestCases = useSelector((state: RootState) => state.scenarioSlice.selectedTestCases);

  const [showTestCasesToScenarioModal, setShowTestCasesToScenarioModal] = useState<boolean>(false);

  const [updateScenarioRequest, { isSuccess: isSuccessUpdateScenarioRequests, isLoading: isLoadingUpdate }] = useUpdateScenarioRequestsMutation();

  useEffect(() => {
    if (isSuccessUpdateScenarioRequests) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      refetchScenarios();
    }
  }, [isSuccessUpdateScenarioRequests, refetchScenarios, dispatch]);

  const filteredTestCases: Array<ScenarioTestCase> = useMemo(() => {
    return selectedTestCases.filter(request => !request.isDeleted);
  }, [selectedTestCases]);

  const dragEnded = useCallback((param: DropResult) => {
    const { source, destination } = param;
    if (!!destination) {
      const filterScenarioTestCases: ScenarioTestCase[] = filteredTestCases.map(item => ({ ...item }));
      const spliceItem = filterScenarioTestCases.splice(source.index, 1)[0];
      filterScenarioTestCases.splice(destination.index, 0, spliceItem);
      filterScenarioTestCases.forEach((item, index) => {
        item.queueNumber = index;
        item.isUpdated = true;
      });
      const newSelectedTestCases = [...filterScenarioTestCases, ...selectedTestCases.filter(item => item.isDeleted)];
      dispatch(updateScenarioSelectedTestCase(newSelectedTestCases));
      dispatch(deleteScenarioExecuteResponse());
    }
  }, [filteredTestCases, selectedTestCases, dispatch]);

  const saveScenario = useCallback((): void => {
    updateScenarioRequest({
      ...scenario,
      assignedRequests: selectedTestCases.map(item => ({
        requestId: item.id as number,
        isDeleted: item.isDeleted,
        queueNumber: item.queueNumber,
        isUpdated: item.isUpdated,
        id: !!item.internalId ? +item.internalId : undefined
      }))
    });
  }, [scenario, selectedTestCases, updateScenarioRequest]);

  const deleteTestCaseForScenario = useCallback((testCaseIndex: number): void => {
    const newScenarioTestCases: Array<ScenarioTestCase> = selectedTestCases.map(item => ({ ...item }));

    const testCase = newScenarioTestCases.filter(item => !item.isDeleted)[testCaseIndex];
    if (!!testCase.internalId) {
      testCase.isDeleted = true;
      testCase.queueNumber = -1;
    } else {
      newScenarioTestCases.splice(testCaseIndex, 1);
    }
    newScenarioTestCases.filter(testCase => !testCase.isDeleted).forEach(((testCase, index) => {
      testCase.queueNumber = index;
      testCase.isUpdated = true;
    }));
    dispatch(updateScenarioSelectedTestCase(newScenarioTestCases));
    dispatch(deleteScenarioExecuteResponse());
  }, [selectedTestCases, dispatch]);

  const closeAddTestCaseToScenario = useCallback((isSave: boolean, testCasesIds?: Array<number>): void => {
    if (isSave) {
      const newSelectedTestCases = [...selectedTestCases];
      testCasesIds?.forEach(testCaseId => {
        const testCaseAndTemplate = getTemplateAndTestCaseByTestCaseId(testCaseTemplates || [], testCaseId);
        if (testCaseAndTemplate) {
          const findTestCase = getTransformTestCase(selectedVariables, testCaseAndTemplate.template, testCaseAndTemplate.testCase);
          newSelectedTestCases.push({
            ...findTestCase,
            queueNumber: newSelectedTestCases.filter(item => !item.isDeleted).length
          });
        }
      });
      dispatch(updateScenarioSelectedTestCase(newSelectedTestCases));
      dispatch(deleteScenarioExecuteResponse());
    }
    setShowTestCasesToScenarioModal(false);
  }, [selectedTestCases, testCaseTemplates, selectedVariables, dispatch, setShowTestCasesToScenarioModal]);

  const getMethodId = useCallback((testCase: TestCase): number => {
    return testCaseTemplates?.find(item => item.id === testCase.testCaseTemplateId)?.methodId || 0
  }, [testCaseTemplates]);

  const showSaveCancelScenario = useMemo(() => {
    return selectedTestCases.some(item => item.isDeleted || item.isUpdated || !item.internalId);
  }, [selectedTestCases]);

  return (
    <>
      <DragDropContext onDragEnd={dragEnded}>
        <Droppable droppableId={`${scenario.id}`}>
          {(provided) => (
            <div className={styles.container} ref={provided.innerRef}>
              <div className={styles.testCases}>
                {
                  filteredTestCases.map((testCase, index) => (
                    <Draggable draggableId={`${testCase?.internalId}-${index}`} index={index} key={`${testCase?.internalId}-${index}`}>
                      {(_provided, snapshot) => (
                        <div className={`${styles.testCase} ${snapshot.isDragging ? styles.hovering : ""}`}
                             key={index}
                             {..._provided.draggableProps}
                             ref={_provided.innerRef}>
                          <div className={styles.info}>
                            <img src={dragIcon} className={styles.dragIcon} alt="" {..._provided.dragHandleProps}/>
                            <div className={styles.methodType}>
                              <MethodType methodId={getMethodId(testCase)}/>
                            </div>
                            <span className={styles.name}>{testCase?.name}</span>
                          </div>
                          <DeleteIcon onClick={() => deleteTestCaseForScenario(index)}/>
                        </div>
                      )}
                    </Draggable>
                  ))
                }
                {provided.placeholder}
              </div>
              <div className={styles.bottomContainer}>
                <div className={styles.save}>
                  { showSaveCancelScenario &&
                    <>
                      {isLoadingUpdate
                        ? (<img src={spinner} alt="" />)
                        : (<span onClick={saveScenario}>{t("common.save")}</span>)
                      }
                      <span onClick={cancelChanges}>{t("common.cancel")}</span>
                    </>
                  }
                </div>
                <div className={styles.addTestCase} onClick={() => setShowTestCasesToScenarioModal(true)}>
                  <AddIcon/>
                  {t("common.addTestCase")}
                </div>
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {showTestCasesToScenarioModal && (<AddTestCasesToScenarioModal onClose={closeAddTestCaseToScenario}
                                                                     scenarioName={scenario.name} />)}
    </>

  );
}